export enum ToolName {
  BookAppointment = "bookAppointment",
  CancelAppointment = "cancelAppointment",
  CheckAppointmentActionScope = "checkAppointmentActionScope",
  CheckEligibility = "checkEligibility",
  CheckExistingPatientEligibility = "checkExistingPatientEligibility",
  CheckSchedulingScope = "checkSchedulingScope",
  ClinicFaqLookup = "clinicFaqLookup",
  CollectDateOfBirth = "collectDateOfBirth",
  CollectName = "collectName",
  CollectSpokenPhoneNumber = "collectSpokenPhoneNumber",
  CollectSubscriberId = "collectSubscriberId",
  CompleteAppointmentRequests = "completeAppointmentRequests",
  ConfirmAppointment = "confirmAppointment",
  ConfirmApproximateAppointmentDate = "confirmApproximateAppointmentDate",
  ConfirmDateOfBirthReadback = "confirmDateOfBirthReadback",
  ConfirmInsuranceUnchanged = "confirmInsuranceUnchanged",
  ConfirmNameReadback = "confirmNameReadback",
  ConfirmNonUrgent = "confirmNonUrgent",
  ConfirmReasonMatchesPriorVisit = "confirmReasonMatchesPriorVisit",
  ConfirmReferralReasonMatch = "confirmReferralReasonMatch",
  ConfirmRescheduleSelection = "confirmRescheduleSelection",
  ConfirmSubscriberIdReadback = "confirmSubscriberIdReadback",
  CreatePatientFromIntake = "createPatientFromIntake",
  CreatePatientCase = "createPatientCase",
  CreateTelephoneEncounter = "createTelephoneEncounter",
  ExistingAppointmentLookup = "existingAppointmentLookup",
  GetBookedAppointments = "getBookedAppointments",
  GetPatientMedications = "getPatientMedications",
  GetLocations = "getLocations",
  GetMissingInformation = "getMissingInformation",
  InitialAppointmentLookup = "initialAppointmentLookup",
  IntentChangeTool = "intentChangeTool",
  PatientLookup = "patientLookup",
  RecordAppointmentReason = "recordAppointmentReason",
  RecordCarrier = "recordCarrier",
  RecordCsatScore = "recordCsatScore",
  RecordReasonForVisit = "recordReasonForVisit",
  RecordReferralStatus = "recordReferralStatus",
  RecordRelationshipToSubscriber = "recordRelationshipToSubscriber",
  ReadBackCancellation = "readBackCancellation",
  ReadBackTelephoneEncounter = "readBackTelephoneEncounter",
  RescheduleAppointment = "rescheduleAppointment",
  SelectAppointmentSlot = "selectAppointmentSlot",
  SelectAppointmentToReschedule = "selectAppointmentToReschedule",
  SelectOpenSlot = "selectOpenSlot",
  TransferCallToRepresentative = "transferCallToRepresentative",
  UpdateDateOfBirth = "updateDateOfBirth",
  UpdatePatientName = "updatePatientName",
  UpdatePhoneNumber = "updatePhoneNumber",
  UpdateSubscriberName = "updateSubscriberName",
  UseCalledFromPhoneNumber = "useCalledFromPhoneNumber",
  VerifyCallerIntent = "verifyCallerIntent",
  VoiceFaqLookup = "voiceFaqLookup",
}

/**
 * Slow Thinker executes deterministic workflow commands instead of exposing the legacy agent's
 * model-facing tools. Preserve the command name in the raw trace, but use the legacy equivalent
 * anywhere tool calls are grouped across agent generations (audit rows, metrics, and dashboards).
 *
 * Several commands intentionally collapse to one legacy tool. For example, initial and follow-up
 * booking are separate Slow Thinker commands but were both `bookAppointment` in the legacy agent.
 */
const SLOW_THINKER_TOOL_NAME_ALIASES: Readonly<Record<string, ToolName>> = {
  book_follow_up: ToolName.BookAppointment,
  book_initial: ToolName.BookAppointment,
  cancel_appointment: ToolName.CancelAppointment,
  check_cancellation_scope: ToolName.CheckAppointmentActionScope,
  check_follow_up_eligibility: ToolName.CheckExistingPatientEligibility,
  check_initial_eligibility: ToolName.CheckEligibility,
  check_reschedule_eligibility: ToolName.CheckExistingPatientEligibility,
  check_scheduling_scope: ToolName.CheckSchedulingScope,
  confirm_appointments: ToolName.ConfirmAppointment,
  create_callback_patient: ToolName.CreatePatientFromIntake,
  create_initial_patient: ToolName.CreatePatientFromIntake,
  create_scheduling_follow_up_case: ToolName.CreatePatientCase,
  create_telephone_encounter: ToolName.CreateTelephoneEncounter,
  get_booked_appointments: ToolName.GetBookedAppointments,
  get_patient_medications: ToolName.GetPatientMedications,
  get_prior_appointments: ToolName.GetBookedAppointments,
  lookup_faq: ToolName.ClinicFaqLookup,
  reschedule_appointment: ToolName.RescheduleAppointment,
  resolve_location: ToolName.GetLocations,
  resolve_scheduling_path: ToolName.VerifyCallerIntent,
  search_follow_up_slots: ToolName.ExistingAppointmentLookup,
  search_initial_slots: ToolName.InitialAppointmentLookup,
  search_patient: ToolName.PatientLookup,
  search_reschedule_slots: ToolName.ExistingAppointmentLookup,
  submit_csat: ToolName.RecordCsatScore,
};

export function canonicalVoiceToolName(name: string): string {
  return SLOW_THINKER_TOOL_NAME_ALIASES[name] ?? name;
}
